import React, { useEffect, useRef, useState } from "react";

// interfaces
import { IVideoPlayer } from "../../../../interfaces/media/IVideo";
import { IconLibrary } from "../../../../interfaces/icon/IconLibrary";

//components
import Icon from "../../icons/Icon";

//hooks
import {usePauseOtherMedia} from "../../../../hooks/usePauseOtherMedia";


const VideoPlayer = ({ className, contentUrl, caption, autoPlayEnabled}: IVideoPlayer) => {
    const videoRef = useRef<HTMLVideoElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const [showControls, setShowControls] = useState(autoPlayEnabled);
    const [isPlaying, setIsPlaying] = useState(false);
    const pauseOtherMedia = usePauseOtherMedia(videoRef);


    useEffect(() => {
        if (!autoPlayEnabled || !videoRef.current || !containerRef.current) {
            return;
        }

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (!videoRef.current) return;
                if (entry.isIntersecting) {
                    videoRef.current.play()
                    .catch(() => {
                        console.error("Browser may have blocked autoplay")
                    });
                } else {
                    videoRef.current.pause();
                }
            },
            {
                //this is how much of the video needs to be in the display before we play it - so like 100% is ideal
                threshold: 1
            }
        );

        observer.observe(containerRef.current);
        return () => {
            observer.disconnect();
        };
    }, [autoPlayEnabled]);

    const togglePlay = () => {
        if (!videoRef.current) return;

        if (videoRef.current.paused) {
            setShowControls(true);
            videoRef.current.play()
            .catch(() => {
                console.error("Browser may have blocked autoplay")
            });
        } else {
            videoRef.current.pause()
        }
    };

    const handlePlay = () => {
        setIsPlaying(true);

        pauseOtherMedia();
    };

    return (
        <div ref={containerRef} className={`video-player-wrapper ${className}`}>
            <div className={`video-player-component ${className}`}>
                <video
                    className="video-player"
                    ref={videoRef}
                    src={contentUrl}
                    controls={showControls}
                    onPlay={handlePlay}
                    onPause={() => setIsPlaying(false)}
                    onEnded={() => setIsPlaying(false)}
                    playsInline
                    muted={autoPlayEnabled}
                />

                {!isPlaying && (
                    <button
                        className="video-play-button"
                        onClick={togglePlay}
                        aria-label="Play video"
                    >
                        <Icon 
                            iconName={IconLibrary.PLAY}
                            sizeInPx="40"  
                            color="var(--thriveWhite)"        
                        />
                    </button>
                )}
            </div>

            {caption && <span className="video-caption">{caption}</span>}
        </div>
    );
};

export default VideoPlayer;